import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';

import { registerScreen, startRouter } from './router';
import { renderExerciseA } from './screens/exercise-a';
import { renderExerciseB } from './screens/exercise-b';
import { renderHome } from './screens/home';
import { renderLevelIntro } from './screens/level-intro';
import { renderNotFound } from './screens/not-found';
import { renderPractice } from './screens/practice';
import { renderStyleguide } from './screens/styleguide';
import { loadCsv } from './data/csv';

// Side-effect import: kicks off the rune-font load probe (M0-4).
import './font-loader';

registerScreen('', renderHome);
registerScreen('level-intro', renderLevelIntro);
registerScreen('exercise-a', renderExerciseA);
registerScreen('exercise-b', renderExerciseB);
registerScreen('practice', renderPractice);
registerScreen('not-found', renderNotFound);
registerScreen('styleguide', renderStyleguide);

/**
 * Defer rendering the loading skeleton until parsing is *actually*
 * slow. FR-DATA-01 AC: "a loading state is shown if parsing takes more
 * than 200 ms". On a fast load we want to skip straight to the first
 * screen without a UI flash.
 */
const LOADING_DELAY_MS = 200;

async function boot(): Promise<void> {
  const app = document.querySelector<HTMLElement>('#app');
  if (!app) {
    // Surfaced loudly because the index.html contract is broken if this fires.
    throw new Error('#app mount point missing from index.html');
  }

  let skeletonTimer: number | null = window.setTimeout(() => {
    renderLoading(app);
    skeletonTimer = null;
  }, LOADING_DELAY_MS);

  try {
    await loadCsv();
  } catch (err) {
    if (skeletonTimer !== null) window.clearTimeout(skeletonTimer);
    renderLoadError(app, err);
    return;
  }
  if (skeletonTimer !== null) window.clearTimeout(skeletonTimer);

  startRouter(app);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void boot(), { once: true });
} else {
  void boot();
}

/**
 * Minimal loading skeleton. Inlined here because it's <30 lines and
 * only used once at boot — a dedicated module would be ceremony.
 */
function renderLoading(mount: HTMLElement): void {
  mount.replaceChildren();
  const card = document.createElement('article');
  card.className = 'card';
  card.setAttribute('aria-busy', 'true');
  card.setAttribute('aria-live', 'polite');

  const tag = document.createElement('span');
  tag.className = 'tag tag--eyebrow';
  tag.textContent = 'Loading';
  card.appendChild(tag);

  const heading = document.createElement('p');
  heading.style.marginTop = 'var(--space-4)';
  heading.style.color = 'var(--color-text-warm)';
  heading.textContent = 'Loading runes…';
  card.appendChild(heading);

  mount.appendChild(card);
}

/**
 * Boot-time error screen. The router never starts, so there is no
 * route to navigate to — we render directly into #app.
 */
function renderLoadError(mount: HTMLElement, err: unknown): void {
  mount.replaceChildren();
  const card = document.createElement('article');
  card.className = 'card card--elevated';
  card.setAttribute('role', 'alert');

  const tag = document.createElement('span');
  tag.className = 'tag';
  tag.textContent = 'Error';
  card.appendChild(tag);

  const heading = document.createElement('h1');
  heading.style.marginTop = 'var(--space-4)';
  heading.textContent = 'Content failed to load';
  card.appendChild(heading);

  const body = document.createElement('p');
  body.style.marginTop = 'var(--space-3)';
  body.style.color = 'var(--color-text-warm)';
  body.textContent = err instanceof Error ? err.message : String(err);
  card.appendChild(body);

  mount.appendChild(card);
}
